import _ from 'lodash'
import api from '../../api/builder'
import { isEqual, getCleanTitle, getInteraction, getInteractionName, getInteractionId, isMergeable, applyDeltas, MODEL_MAP, DATA_OBJECT_CHANGE_PROPS } from '../../helpers/builder'
import EventBus from '../../helpers/event-bus'

export default {
  namespaced: true,
  state: {
    selectedId: null,
    setTemplateSelectionId: null,
    timestamp: 0,
    advancedMode: false,
    screenTemplateMode: true,
    aiMode: false,
    source: null,
    settings: null,
    sourceType: 'learning',
    client: { },
    project: { },
    details: { },
    dataObjectSource: [],
    dataObjects: [],
    datasets: [],
    outline: [],
    additions: [],
    removals: [],
    status: null,
    remoteSources: [],
    statusLog: [],
    isSyncing: false,
    users: [],
    revisionHistory: { },
    previousDatasetCategory: ''
  },
  getters: {
    sourceDetails: ({ source }) => _.pick(source, ['name', 'description']),
    sourceUrl: ({ source, sourceType }) => `${MODEL_MAP[sourceType].url}/${source.id}`,
    schemaRefs: ({ project }) => _.get(project.driver, 'schemas', { }),
    manifest: ({ project }) => _.get(project.driver, 'manifest', { }),
    interactions: ({ source }) => _.get(source, 'interactions', []),
    screenTemplates: ({ project }) => _.get(project.screen_templates, 'data_objects', { }),
    changes: ({ dataObjects, dataObjectSource, removals }) => _.reduce(dataObjects, (result, object) => {
      const originalObject = _.find(dataObjectSource, { id: object.id })
      if (originalObject && !removals.includes(object.id)) {
        DATA_OBJECT_CHANGE_PROPS.forEach((prop) => {
          if (!isEqual(originalObject[prop], object[prop])) {
            _.set(result, `${object.id}.${prop}`, object[prop])
          }
        })
      }
      return result
    }, { }),
    hasOrderChanged: ({ dataObjectSource }, { nonVirtualData }) => _.some(nonVirtualData, ({ id, sort_order }) => {
      const originalObject = _.find(dataObjectSource, { id })
      return originalObject && originalObject.sort_order !== sort_order
    }),
    hasDetailChanged: (state, getters) => !isEqual(state.details, getters.sourceDetails),
    dirty: ({ additions, removals }, { changes, hasDetailChanged, hasOrderChanged }) => {
      return _.size(changes) > 0 || hasOrderChanged || hasDetailChanged || additions.length || removals.length
    },
    screenTypes: (state, { interactions }) => _.groupBy(interactions, ({ category, type }) => category || type),
    currentDataObject: ({ selectedId, dataObjects }) => _.find(dataObjects, { id: selectedId }),
    currentInteraction: (state, { currentDataObject }) => getInteraction(currentDataObject),
    currentObjectStatus: ({ additions, removals }, { changes, currentDataObject }) => {
      if (currentDataObject) {
        if (additions.indexOf(currentDataObject.id) >= 0) {
          return 'added'
        } else if (removals.indexOf(currentDataObject.id) >= 0) {
          return 'removed'
        } else if (_.has(changes, currentDataObject.id)) {
          return 'changed'
        }
      }

      return ''
    },
    getInteractionNameForPage: (state, { interactions }) => (dataObject) => {
      return getInteractionName(interactions, dataObject)
    },
    nonVirtualData: ({ dataObjects }) => dataObjects.filter(item => {
      return !item.virtual && !item.is_virtual
    }),
    statusMap: ({ additions, removals }, { changes }) => {
      const result = { }

      // get changes
      for (const id in changes) {
        result[id] = 'changed'
      }

      additions.forEach(id => result[id] = 'added')
      removals.forEach(id => result[id] = 'removed')

      return result
    },
    screenTemplateGroups: ({ source }, { screenTemplates }) => {
      const result = { }

      for (const index in screenTemplates) {
        const obj = screenTemplates[index]
        const key = obj.id
        const type = obj.type

        if (!result[type])
          result[type] = [];

        result[type].push({ "id": key, "text": obj.title, "value":obj })
      }

      console.log('RESULT')
      console.log(result)
      return result
    },
    screenTemplateForType: (state, { screenTemplateGroups }) => (type) => {
      // find all screen templates matching the type
      return screenTemplateGroups[type] ?? null
    },
    screenTemplatesForPage: (state, { screenTemplateGroups }) => (id) => {
      // find all screen templates matching the object id
      const dataObject = _.find(state.dataObjects, { id: id })

      if (!state.dataObjects || !dataObject)
        return null

      return screenTemplateGroups[getInteractionId(dataObject)] ?? null
    },
    screenTemplateDataForId: (state, { screenTemplates }) => (id) => {
      // lookup template by id and return template.data
      const dataObject = _.find(screenTemplates, { id })
      if (dataObject)
        return dataObject.data

      return null
    },
    presenceChannel: ({ source, sourceType }) => `${sourceType}.content.${source.id}`,
    notificationChannel: ({ source, sourceType }) => `${MODEL_MAP[sourceType].model}.${source.id}`,
    aiPublications: ({ source }) => {
      const aiPublications = _.get(source, 'ai_publications', [])
      return aiPublications
    }
  },
  mutations: {
    setAdvancedMode (state, value) {
      state.advancedMode = value
    },
    setScreenTemplateMode (state, value) {
      state.screenTemplateMode = value
    },
    setAiMode (state, value) {
      state.aiMode = value
    },
    setTemplateSelectionId (state, value) {
      state.setTemplateSelectionId = value
    },
    setSettings (state, value) {
      state.settings = value
    },
    setClient (state, value) {
      state.client = value
    },
    setProject (state, value) {
      state.project = value
    },
    setSource (state, value) {
      state.source = value
    },
    updateSource (state, value) {
      state.source = {
        ...state.source,
        ...value
      }
    },
    setSourceType (state, value) {
      state.sourceType = value
    },
    setDatasets (state, value) {
      state.datasets = value
    },
    setSelectedId (state, value) {
      state.selectedId = value
    },
    setStatus (state, value) {
      state.status = value
    },
    clearStatus (state) {
      state.status = null
    },
    setIsSyncing (state, value) {
      state.isSyncing = value
    },
    updateDetails (state, value) {
      state.details = {
        ...state.details,
        ...value
      }
    },
    setDetails (state, value) {
      state.details = value
    },
    setTimestamp (state, value) {
      state.timestamp = value
    },
    setDataObjects (state, value) {
      state.dataObjects = value
    },
    addDataObject (state, value) {
      state.dataObjects.push(value)
    },
    updateDataObject (state, value) {
      state.dataObjects = _.map(state.dataObjects, (dataObject) => {
        if (dataObject.id === value.id) {
          return { ...dataObject, ...value }
        }
        return dataObject
      })
    },
    removeDataObject (state, { id }) {
      const index = _.findIndex(state.dataObjects, { id })
      if (index >= 0) {
        state.dataObjects.splice(index, 1)
      }
    },
    setDataObjectSource (state, value) {
      state.dataObjectSource = value
    },
    addDataObjectSource (state, value) {
      state.dataObjectSource.push(value)
    },
    updateDataObjectSource (state, value) {
      // console.log(_.cloneDeep(value))
      state.dataObjectSource = _.map(state.dataObjectSource, (dataObject) => {
        if (dataObject.id === value.id) {
          return { ...dataObject, ...value }
        }
        return dataObject
      })
    },
    removeDataObjectSource (state, { id }) {
      const index = _.findIndex(state.dataObjectSource, { id })
      if (index >= 0) {
        state.dataObjectSource.splice(index, 1)
      }
    },
    setOutline (state, value) {
      state.outline = value
    },
    setUsers (state, value) {
      state.users = value
    },
    addUser (state, value) {
      state.users.push(value)
    },
    removeUser (state, { id }) {
      const index = _.findIndex(state.users, { id })
      if (index >= 0) {
        state.users.splice(index, 1)
      }
    },
    setRemoteSources (state, value) {
      state.remoteSources = value
    },
    setAdditions (state, value) {
      state.additions = value
    },
    addAddition (state, value) {
      if (!state.additions.includes(value)) {
        state.additions.push(value)
      }
    },
    removeAddition (state, value) {
      const index = state.additions.indexOf(value)
      if (index >= 0) {
        state.additions.splice(index, 1)
      }
    },
    setRemovals (state, value) {
      state.removals = value
    },
    addRemoval (state, value) {
      if (!state.removals.includes(value)) {
        state.removals.push(value)
      }
    },
    removeRemoval (state, value) {
      const index = state.removals.indexOf(value)
      if (index >= 0) {
        state.removals.splice(index, 1)
      }
    },
    addStatusUpdate (state, value) {
      state.statusLog.push(value)
    },
    setRevisionHistory (state, { id, history }) {
      state.revisionHistory[id] = history
    },
    setPreviousDatasetCategory (state, value) {
      state.previousDatasetCategory = value
    }
  },
  actions: {
    initialize ({ state, getters, commit, dispatch }, { source, type, settings }) {
      if (type) {
        commit('setSourceType', type)
      }

      commit('setSource', source)

      if (settings)
        commit('setSettings', settings)

      const dataObjects = source?.data_objects ?? []
      console.log('DATAOBJECTS:')
      console.log(dataObjects)

      // build full source (including virtual data)
      const dataObjectSource = dataObjects.reduce((result, dataObject) => {
        if (dataObject.virtual) {
          result = result.concat(dataObject.data_objects)
        }
        result.push(dataObject)
        return result
      }, [])
      commit('setDataObjectSource', dataObjectSource)

      commit('setDataObjects', _.cloneDeep(state.dataObjectSource))
      commit('setDetails', _.cloneDeep(getters.sourceDetails))

      const project = source.project || source

      commit('setProject', project)
      commit('setClient', project.client)
      commit('setDatasets', project.data_objects)

      // get remote sources
      // dispatch('refreshSources')

      // set initial outline (remove anything without an ID)
      dispatch('syncOutline')

      // clear additions and removals
      commit('setAdditions', [])
      commit('setRemovals', [])
    },
    processNotification ({ commit, dispatch, rootState }, notification) {
      console.info(notification)

      // handle status updates
      if (notification.type === 'status.update') {

        // if process is completed, update model timestamps
        if (notification.progress === 1) {
          commit('setIsSyncing', false)
          commit('updateSource', _.pick(notification.model, ['updated_at', 'synced_at', 'outdated']))
        } else {
          commit('setIsSyncing', true)
        }

        commit('addStatusUpdate', notification)
        EventBus.$emit('log:added', notification)
      }

      // handle asynchronous remote data changes
      if (notification.type === 'data.changed') {
        // ignore changesets initiated by self
        if (notification.user.id !== rootState.user.id) {
          _.each(notification.changes, (dataObject, id) => {
            dispatch('processDataChanges', { id: parseInt(id, 10), dataObject })
          })
        }
      }
    },
    syncOutline ({ getters, commit }) {
      const sorted = _.sortBy(getters.nonVirtualData, 'sort_order')
      const mapped = sorted.map(item => item.id)
      commit('setOutline', mapped)
    },
    async selectObject ({ state, commit, dispatch }, id) {
      const dataObject = _.find(state.dataObjects, { id })
      console.log('selectObject', dataObject)
      if (dataObject) {
        const result = await dispatch('validateDataObject', dataObject)

        if (result === 'template-selection')
        {
          commit('setSelectedId', null)
          commit('setTemplateSelectionId', id)
          return;
        }
  
        commit('setTemplateSelectionId', null)

        if (result.data) {
          commit('updateDataObject', { id: result.id, data: result.data })
        }
        // dispatch('refreshDataObject', id)

        history.pushState({ id }, '', `#${id}`)
        commit('setSelectedId', id)
      }
      else
      {
        commit('setSelectedId', null)
      }
    },
    async validateDataObject ({ context, getters }, dataObject) {
      // check for missing data
      if (!dataObject.data) {

        // templates
        const _type = getInteractionId(dataObject)
        const templates = getters.screenTemplateForType(_type) ?? []

        console.log('Type: ', _type)
        console.log('Available templates: ')
        console.log(templates)
        // if one template available, set it
        if (templates.length === 1)
        {
          dataObject.data = getters.screenTemplateDataForId(templates[0]?.id) ?? null
        }
        // if more than 1, allow user to make a template selection
        else if (templates.length >= 1)
        {
          return 'template-selection'
        }

        // fallback, auto generate data
        if (!dataObject.data) {
          dataObject.data = await new Promise((resolve, reject) => {
            EventBus.$emit('data:generate', dataObject.schema_ref, resolve, reject)
          })
        }

        console.log('Data set to:')
        console.log(dataObject.data)

        // set default title, if exists
        if (dataObject.title && _.has(dataObject.data, '$_title')) {
          _.set(dataObject.data, '$_title', dataObject.title)
        }

        // await this.$nextTick()
      }

      return dataObject
    },
    async refresh ({ state, commit }) {
      if (state.source) {
        const result = await api.refresh(state.project.id, { timestamp: state.timestamp })
        
        if (result) {
          if (result.server !== state.timestamp) {
            commit('setTimestamp', result.server)
            if (result.refresh) {
              // TODO: refresh/update local data
            }
          }
          return true
        }
      }

      return false
    },
    async refreshSources ({ state, commit }) {
      const result = await api.getProjectOutline(state.project.id)
      if (state.sourceType === 'learning') {
        commit('setRemoteSources', _.reject(result.learning_objects, { id: state.source.id }))
      } else {
        commit('setRemoteSources', result)
      }
    },
    async refreshDataObject ({ getters, dispatch }, id) {
      const dataObject = await api.getDataObject(getters.sourceUrl, id)
      dispatch('processDataChanges', { id, dataObject })
    },
    async processDataChanges ({ state, getters, commit, dispatch }, { id, dataObject }) {
      const prevSelectedIndex = state.outline.indexOf(state.selectedId)

      // get current object
      const current = _.find(state.dataObjects, { id })

      // check if it has local changes
      const isChanged = getters.statusMap[id] === 'changed'

      if (dataObject) {
        // check if remote data is newer
        if (!current) {
          console.info('New screen added!')
  
          // this is a new screen, add it
          commit('addDataObjectSource', dataObject)
          commit('addDataObject', dataObject)
        } else if (dataObject.revision !== current.revision) {
          console.info('Remote data is updated')

          commit('updateDataObjectSource', dataObject)

          if (isChanged) {
            // identify if the change is mergeable
            if (isMergeable(dataObject, current)) {
              // update the local object with merged delta changes
              current.data = applyDeltas(current.data, [dataObject.delta])
              commit('updateDataObject', current)
              if (current.id === state.selectedId) {
                EventBus.$emit('editor:setValue', current.data)
              }
            } else {
              console.warn('Remote data has changed! Out of sync with local modifications.')
              EventBus.$emit('toast:notification', {
                title: 'Warning',
                icon: 'icon-exclamation',
                color: 'danger',
                body: `The object <strong>${getCleanTitle(current)}</strong> was updated and is in conflict with your local modifications.`,
                autoClose: false
              })
            }
          } else {
            // just update the local object
            commit('updateDataObject', dataObject)
            if (current.id === state.selectedId) {
              EventBus.$emit('editor:setValue', dataObject.data)
            }
          }
        } else {
          console.info('Remote data is unchanged')
          // sync other non-revision properties
          const infoProps = _.pick(dataObject, ['id', 'name', 'title', 'sort_order'])
          commit('updateDataObjectSource', infoProps)
          commit('updateDataObject', infoProps)
        }
      } else if (current) {
        if (isChanged) {
          EventBus.$emit('toast:notification', {
            icon: 'icon-exclamation',
            color: 'danger',
            body: `The object <strong>${getCleanTitle(current)}</strong> with local modifications was deleted by another user.`,
            autoClose: false
          })
          console.warn('An object with local modifications was deleted by another user.')
        } else if (state.selectedId === id) {
          EventBus.$emit('toast:notification', {
            icon: 'icon-exclamation',
            color: 'danger',
            body: `The object <strong>${getCleanTitle(current)}</strong> was deleted by another user.`,
            autoClose: false
          })
        }

        // object was removed
        commit('removeDataObjectSource', { id })
        commit('removeDataObject', { id })
      }

      dispatch('syncOutline')

      if (!getters.currentDataObject) {
        const index = _.clamp(prevSelectedIndex, 0, state.outline.length - 1)
        dispatch('selectObject', state.outline.length ? state.outline[index] : null)
      }

      return dataObject
    },
    async addPage ({ state, commit, dispatch }, dataObject) {
      const result = await dispatch('validateDataObject', dataObject)

      result.id = _.uniqueId('tmp-')
      result.sort_order = result.sort_order || state.outline.length

      commit('addAddition', result.id)
      commit('addDataObject', result)

      dispatch('syncOutline')

      // set new page as selected
      dispatch('selectObject', result.id)

      return result
    },
    removePage ({ state, getters, commit, dispatch }, dataObject) {
      if (getters.statusMap[dataObject.id] === 'added') {
        if (!confirm('Are you sure you want to delete this screen?')) {
          return
        }

        commit('removeAddition', dataObject.id)
        commit('removeDataObject', dataObject)

        let index = state.outline.indexOf(dataObject.id)

        dispatch('syncOutline')
  
        // update selection
        index = _.clamp(index, 0, state.outline.length - 1)
  
        dispatch('selectObject', state.outline.length ? state.outline[index] : null)
      } else {
        commit('addRemoval', dataObject.id)
      }
    },
    async getRevisionHistory ({ commit }, id) {
      const revisions = await api.getRevisions(id)

      if (revisions) {
        commit('setRevisionHistory', { id, history: revisions })
      }
    },
    async getRevisionData ({ commit }, source) {
      const revision = await api.getRevision(source.data_object_id, source.id)

      // set revision data
      // commit('updateDataObject', result)

      return revision
    },
    async commitChanges ({ state, getters, commit, dispatch }) {
      commit('setStatus', 'saving')

      // update details
      if (getters.hasDetailChanged) {
        console.info('Saving updated details...')
        const result = await api.update(getters.sourceUrl, state.details)
        if (result) {
          commit('setSource', result)
        }
      }

      // get fresh outline order including any data object changes
      let sortOrder = 0
      const sorted = _.sortBy(state.dataObjects, 'sort_order')
      const changes = _.reduce(sorted, (result, object) => {
        // if object is not removed
        if (getters.statusMap[object.id] !== 'removed') {
          const changes = getters.changes[object.id]

          if (object.is_virtual) {
            // virtual data, only save if has changes
            if (changes) {
              result.push({ id: object.id, ...changes })
            }
          } else if (!object.virtual) {
            // screen data save order + changes
            result.push(Object.assign({
              id: object.id,
              sort_order: sortOrder++
            }, changes))
          }
        }

        return result
      }, [])

      const removals = state.removals.map(id => {
        const dataObject = _.find(state.dataObjects, { id })
        const data = { id }
        if (dataObject?.is_virtual) {
          data.model_id = dataObject.model_id
        }
        return data
      })

      // update existing pages
      console.info('Saving page data...')
      const changeSet = {
        add: _.map(state.additions, (id) => _.find(state.dataObjects, { id })),
        remove: removals,
        change: changes
      }

      const result = await api.saveChanges(getters.sourceUrl, changeSet)

      if (result) {
        const selectedName = getters.currentDataObject?.name

        dispatch('initialize', { source: result })

        // prev selection was a new object
        if (typeof state.selectedId === 'string') {
          const previousSelection = state.dataObjects.find(item => item.name === selectedName)
          if (previousSelection) {
            dispatch('selectObject', previousSelection.id)
          }
        }
      }

      commit('clearStatus')
    }
  }
}
